/**
 * useForm hook with local auto-save and optional cloud save functionality.
 *
 * Auto-save: debounced 30s → localStorage via deviceForms service
 * handleSaveToCloud: cloud create/update + delete device draft → navigate
 * handleSaveToDevice: mark device form as completed → navigate
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createForm,
  updateForm,
  getForm,
  deleteForm,
  FormData,
  generateAIFeedback,
} from '../services/forms';
import {
  saveDeviceForm,
  updateDeviceForm,
  deleteDeviceForm,
} from '../services/deviceForms';
import { useAuth } from '../hooks/useAuth';

export type StorageLocation = 'cloud' | 'device' | null;

export interface DialogState {
  isOpen: boolean;
  mode: 'move' | 'overwrite';
  from: 'cloud' | 'device';
  to: 'cloud' | 'device';
  onConfirm: () => void;
}

const AUTO_SAVE_DELAY = 30000; // 30 seconds

export function useForm(
  initialFormId: string | null = null,
  formType: 'TUP' | 'DUP' | 'DOS' | 'OK10' = 'DUP',
  onAfterSave?: () => void
) {
  const { user } = useAuth();

  // Cloud form id
  const [formId, setFormId] = useState<string | null>(initialFormId);
  // Device form id (localStorage)
  const [deviceFormId, setDeviceFormId] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({});
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  const [storageLocation, setStorageLocation] = useState<StorageLocation>(
    initialFormId ? 'cloud' : null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);

  // Keep isSavingRef in sync so auto-save callback always reads latest value
  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  // ─── Initial cloud form load ────────────────────────────────────────────────
  useEffect(() => {
    if (initialFormId) {
      (async () => {
        try {
          const response = await getForm(initialFormId);
          setFormData(response.form_data);
          setAiFeedback(response.ai_feedback || null);
          setFormId(initialFormId);
          setStorageLocation('cloud');
        } catch (err) {
          setError('Nie udało się załadować formularza');
          console.error('Load error:', err);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormId]);

  // ─── Cleanup auto-save timeout on unmount ───────────────────────────────────
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // ─── Auto-save to device ────────────────────────────────────────────────────
  const autoSaveToDevice = useCallback(
    (data: FormData) => {
      if (!user?.id) return;
      if (isSavingRef.current) return;

      if (deviceFormId) {
        try {
          updateDeviceForm(user.id, deviceFormId, { formData: data });
          setLastSaved(new Date());
        } catch (err) {
          console.error('Auto-save update error:', err);
        }
      } else {
        try {
          const created = saveDeviceForm(user.id, formType, data, 'draft');
          setDeviceFormId(created.id);
          setLastSaved(new Date());
          // Only transition storageLocation to 'device' when not already cloud
          setStorageLocation(prev => (prev === 'cloud' ? 'cloud' : 'device'));
        } catch (err) {
          console.error('Auto-save create error:', err);
        }
      }
    },
    [user?.id, deviceFormId, formType]
  );

  // ─── updateField ────────────────────────────────────────────────────────────
  const updateField = useCallback(
    (fieldName: string, value: any) => {
      setFormData(prev => {
        const next = { ...prev, [fieldName]: value };

        // Clear existing debounce
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Schedule auto-save with fresh data
        saveTimeoutRef.current = setTimeout(() => {
          autoSaveToDevice(next);
        }, AUTO_SAVE_DELAY);

        return next;
      });
    },
    [autoSaveToDevice]
  );

  // ─── Dialog helpers ─────────────────────────────────────────────────────────
  const closeDialog = useCallback(() => {
    setDialogState(null);
  }, []);

  // ─── Core: save to cloud ────────────────────────────────────────────────────
  const executeSaveToCloud = useCallback(
    async (data: FormData) => {
      if (!user?.id) return;
      setIsSaving(true);
      setError(null);
      try {
        let cloudId = formId;
        if (cloudId) {
          await updateForm(cloudId, data, 'completed');
        } else {
          const response = await createForm(formType, data, 'completed');
          cloudId = response.id;
          setFormId(cloudId);
        }

        // Delete device draft if it exists
        if (deviceFormId) {
          try {
            deleteDeviceForm(user.id, deviceFormId);
          } catch (err) {
            console.warn('Could not delete device draft:', err);
          }
          setDeviceFormId(null);
        }

        setStorageLocation('cloud');
        setLastSaved(new Date());
        closeDialog();
        onAfterSave?.();
      } catch (err) {
        setError('Nie udało się zapisać formularza w chmurze');
        console.error('Cloud save error:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [formId, formType, deviceFormId, user?.id, closeDialog, onAfterSave]
  );

  // ─── Core: save to device ───────────────────────────────────────────────────
  const executeSaveToDevice = useCallback(
    async (data: FormData) => {
      if (!user?.id) return;
      setIsSaving(true);
      setError(null);
      try {
        if (deviceFormId) {
          updateDeviceForm(user.id, deviceFormId, {
            formData: data,
            completionStatus: 'completed',
          });
        } else {
          const created = saveDeviceForm(
            user.id,
            formType,
            data,
            'completed'
          );
          setDeviceFormId(created.id);
        }

        // Delete cloud form if it exists
        if (formId) {
          try {
            await deleteForm(formId);
          } catch (err) {
            console.warn('Could not delete cloud form:', err);
          }
          setFormId(null);
        }

        setStorageLocation('device');
        setLastSaved(new Date());
        closeDialog();
        onAfterSave?.();
      } catch (err) {
        setError('Nie udało się zapisać formularza na urządzeniu');
        console.error('Device save error:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [formId, formType, deviceFormId, user?.id, closeDialog, onAfterSave]
  );

  // ─── handleSaveToCloud ──────────────────────────────────────────────────────
  const handleSaveToCloud = useCallback(() => {
    const data = formData; // capture current snapshot

    // New form (no existing storage): save directly
    if (!formId && !deviceFormId) {
      executeSaveToCloud(data);
      return;
    }

    // Already stored in cloud: overwrite dialog
    if (storageLocation === 'cloud') {
      setDialogState({
        isOpen: true,
        mode: 'overwrite',
        from: 'cloud',
        to: 'cloud',
        onConfirm: () => executeSaveToCloud(data),
      });
      return;
    }

    // Stored on device → move to cloud dialog
    setDialogState({
      isOpen: true,
      mode: 'move',
      from: 'device',
      to: 'cloud',
      onConfirm: () => executeSaveToCloud(data),
    });
  }, [formData, formId, deviceFormId, storageLocation, executeSaveToCloud]);

  // ─── handleSaveToDevice ─────────────────────────────────────────────────────
  const handleSaveToDevice = useCallback(() => {
    const data = formData; // capture current snapshot

    // New form (no existing storage): save directly
    if (!formId && !deviceFormId) {
      executeSaveToDevice(data);
      return;
    }

    // Already stored on device: overwrite dialog
    if (storageLocation === 'device') {
      setDialogState({
        isOpen: true,
        mode: 'overwrite',
        from: 'device',
        to: 'device',
        onConfirm: () => executeSaveToDevice(data),
      });
      return;
    }

    // Stored in cloud → move to device dialog
    setDialogState({
      isOpen: true,
      mode: 'move',
      from: 'cloud',
      to: 'device',
      onConfirm: () => executeSaveToDevice(data),
    });
  }, [formData, formId, deviceFormId, storageLocation, executeSaveToDevice]);

  // ─── requestAIFeedback ──────────────────────────────────────────────────────
  const requestAIFeedback = useCallback(async () => {
    // AI feedback requires a cloud form
    let cloudId = formId;

    if (!cloudId) {
      // Create a cloud draft first
      setIsSaving(true);
      try {
        const response = await createForm(formType, formData, 'draft');
        cloudId = response.id;
        setFormId(cloudId);
        setStorageLocation('cloud');
      } catch (err) {
        setError('Nie udało się zapisać formularza przed wygenerowaniem feedbacku');
        console.error('Pre-AI save error:', err);
        setIsSaving(false);
        return;
      } finally {
        setIsSaving(false);
      }
    }

    setIsLoadingAI(true);
    setError(null);

    try {
      const response = await generateAIFeedback(cloudId, false);

      if (response.status === 'success') {
        setAiFeedback(response.feedback);
      } else if (response.status === 'feedback_exists') {
        const overwrite = window.confirm('Feedback już istnieje. Czy chcesz go nadpisać?');
        if (overwrite) {
          const newResponse = await generateAIFeedback(cloudId, true);
          setAiFeedback(newResponse.feedback);
        } else {
          setAiFeedback(response.existing_feedback);
        }
      }
    } catch (err) {
      setError('Nie udało się wygenerować feedbacku AI');
      console.error('AI feedback error:', err);
    } finally {
      setIsLoadingAI(false);
    }
  }, [formId, formType, formData]);

  // ─── submitForm (backward-compat alias for handleSaveToCloud) ───────────────
  const submitForm = handleSaveToCloud;

  return {
    formId,
    formData,
    aiFeedback,
    storageLocation,
    updateField,
    handleSaveToCloud,
    handleSaveToDevice,
    submitForm,       // backward-compat alias
    requestAIFeedback,
    isSaving,
    isLoadingAI,
    lastSaved,
    error,
    dialogState,
    closeDialog,
  };
}

export default useForm;
